import { useMemo } from "react";
import { Box, TableSortLabel, Typography } from "@mui/material";
import { parse } from "date-fns";
import ScheduleView from "features/schedule-view/ScheduleView";
import { TimeEntry } from "models/TimeEntry";
import { sv } from "date-fns/locale";
import { formatDateTime } from "utilities/formatDateTime";
import { ScheduleScale } from "features/schedule-view/ScheduleContext";
import {
  absenceStateToString,
  absenceTypeToColor,
  absenceTypeToString,
} from "utilities/formatAbsence";
import { useAuthContext } from "features/auth/AuthContext";
import { Creator } from "models/Checklist";

type AbsenceScheduleProps = {
  rows: Creator[];
  entries: TimeEntry[];
  view: ScheduleScale;
  fromDate: Date;
  toDate: Date;
  showDayNumbers: boolean;
  sortBy: keyof TimeEntry;
  sortDir: "asc" | "desc";
  setSortDir: (dir: "asc" | "desc") => void;
  setSortBy: (key: keyof TimeEntry) => void;
};

const AbsenceSchedule: React.FC<AbsenceScheduleProps> = (props) => {
  const { user } = useAuthContext();
  const isAdmin = user?.role === "ADMIN" || user?.role === "USER_ADMIN" || user?.role === "PAYROLL";

  const {
    rows,
    entries,
    view,
    fromDate,
    toDate,
    showDayNumbers,
    sortBy,
    sortDir,
    setSortDir,
    setSortBy,
  } = props;

  const scheduleData = useMemo(
    () =>
      entries.map((entry) => ({
        ...entry,
        from: parse(entry.from, "yyyy-MM-dd HH:mm", new Date(), { locale: sv }),
        to: parse(entry.to, "yyyy-MM-dd HH:mm", new Date(), { locale: sv }),
      })),
    [entries],
  );

  return (
    <ScheduleView
      rows={rows}
      entries={scheduleData}
      groupById={(entry) => entry.userId}
      fromDate={fromDate}
      toDate={toDate}
      scale={view}
      showDayNumbers={showDayNumbers}
      firstColumnHeader={
        <TableSortLabel
          active={sortBy === "userName"}
          direction={sortBy === "userName" ? sortDir : "asc"}
          onClick={() =>
            sortBy === "userName"
              ? setSortDir(sortDir === "asc" ? "desc" : "asc")
              : setSortBy("userName")
          }
        >
          Medarbetare
        </TableSortLabel>
      }
      renderRowHeader={([row]) => row.userName}
      entryProps={(entry) => ({
        color: isAdmin ? absenceTypeToColor(entry.type) : "success",
        state: isAdmin && entry.state === "PENDING" ? "tentative" : "confirmed",
        tooltip: (
          <Box textAlign="center">
            {isAdmin && (
              <>
                <Typography fontSize="inherit" fontWeight="bold">
                  {absenceTypeToString(entry.type)}
                </Typography>
                <Typography fontSize="inherit">{absenceStateToString(entry.state)}</Typography>
              </>
            )}
            <Typography fontSize="inherit">Från: {formatDateTime(entry.from)}</Typography>
            <Typography fontSize="inherit">Till: {formatDateTime(entry.to)}</Typography>
          </Box>
        ),
      })}
    />
  );
};

export default AbsenceSchedule;
